import React, { useState, useEffect } from 'react'
import { 
  Search, 
  Plus, 
  Grid, 
  List, 
  Filter, 
  Settings, 
  LogOut, 
  Home, 
  Database, 
  Leaf,
  Target,
  ChevronDown,
  Upload,
  Edit3,
  Trash2,
  MapPin,
  Image,
  Save,
  CheckCircle,
  X,
  Loader2
} from 'lucide-react'
import { useAuth } from '../utils/authContext'
import usePlantStore from '../utils/plantStore'

const AdminMinimalist = () => {
  const { user, logout } = useAuth()
  const { plants, addPlant, updatePlant, deletePlant, fetchPlants } = usePlantStore()
 	const [activeSection, setActiveSection] = useState('dashboard')
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingPlant, setEditingPlant] = useState(null)
  const [viewMode, setViewMode] = useState('grid')
  const [filterCategory, setFilterCategory] = useState('all')

  const [formData, setFormData] = useState({
    name: '',
    scientificName: '',
    category: 'tree',
    description: '',
    habitat: '',
    careLevel: 'Easy',
    image: '',
    latitude: '',
    longitude: ''
  })

  const sections = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'plants', label: 'Plants', icon: Leaf },
    { id: 'reforestation', label: 'Reforestation', icon: Target },
    { id: 'settings', label: 'Settings', icon: Settings }
  ]

  const categories = [
    { id: 'all', name: 'All Types' },
    { id: 'tree', name: 'Trees' },
    { id: 'shrub', name: 'Shrubs' },
    { id: 'flower', name: 'Flowers' },
    { id: 'succulent', name: 'Succulents' },
    { id: 'palm', name: 'Palms' }
  ]

  const filteredPlants = plants.filter(plant => {
    const matchesSearch = plant.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         plant.scientificName?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = filterCategory === 'all' || plant.category === filterCategory
    return matchesSearch && matchesCategory
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingPlant) {
        await updatePlant(editingPlant.id, formData)
      } else {
        await addPlant(formData)
      }
      setShowAddModal(false)
      setEditingPlant(null)
      resetForm()
    } catch (error) {
      console.error('Error saving plant:', error)
    }
  }

  const handleEdit = (plant) => {
    setFormData({
      name: plant.name || '',
      scientificName: plant.scientificName || '',
      category: plant.category || 'tree',
      description: plant.description || '',
      habitat: plant.habitat || '',
      careLevel: plant.careLevel || 'Easy',
      image: plant.image || '',
      latitude: plant.latitude || '',
      longitude: plant.longitude || ''
    })
    setEditingPlant(plant)
    setShowAddModal(true)
  }

  const handleDelete = async (plantId) => {
    if (window.confirm('Are you sure you want to delete this plant?')) {
      await deletePlant(plantId)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      scientificName: '',
      category: 'tree',
      description: '',
      habitat: '',
      careLevel: 'Easy',
      image: '',
      latitude: '',
      longitude: ''
    })
  }

  const stats = {
    totalPlants: plants.length,
    categories: categories.reduce((acc, cat) => {
      acc[cat.id] = plants.filter(p => p.category === cat.id).length
      return acc
    }, {}),
    recentActivity: plants.slice(-5)
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <p className="text-lg mb-4">Please log in to access the admin panel.</p>
          <button onClick={() => window.history.back()} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Sidebar */}
      <div className="w-64 bg-gray-800 border-r border-gray-700 flex flex-col">
        {/* Logo Area */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-lg">PalmGarden</h1>
              <p className="text-gray-400 text-xs">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {sections.map((section) => {
            const Icon = section.icon
            const isActive = activeSection === section.id
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{section.label}</span>
              </button>
            )
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold">{user.email?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user.email}</p>
              <p className="text-gray-400 text-sm">{user.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center space-x-3 px-3 py-2 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-white capitalize">
                {activeSection}
              </h2>
              {activeSection === 'plants' && (
                <div className="text-gray-400 text-sm">
                  ({stats.totalPlants} items)
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-gray-700 text-white placeholder-gray-400 px-10 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors w-64"
                />
              </div>

              {activeSection === 'plants' && (
                <>
                  {/* View Mode Toggle */}
                  <div className="flex bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Add Button */}
                  <button
                    onClick={() => {
                      setEditingPlant(null)
                      resetForm()
                      setShowAddModal(true)
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Plant</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6">
          {activeSection === 'dashboard' && <DashboardView stats={stats} />}
          {activeSection === 'plants' && (
            <PlantsView
              plants={filteredPlants}
              viewMode={viewMode}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              categories={categories}
            />
          )}
          {activeSection === 'reforestation' && <ReforestationsView />}
          {activeSection === 'settings' && <SettingsView />}
        </div>
      </div>

      {/* Add/Edit Plant Modal */}
      {showAddModal && (
        <PlantModal
          formData={formData}
          handleInputChange={handleInputChange}
          handleSubmit={handle_SUBMIT}
          onClose={() => {
            setShowAddModal(false)
            setEditingPlant(null)
            resetForm()
          }}
          editingPlant={editingPlant}
        />
      )}
    </div>
  )
}

// Dashboard View Component
const DashboardView = ({ stats }) => {
  const categories = [
    { id: 'tree', name: 'Trees', color: 'bg-green-600' },
    { id: 'shrub', name: 'Shrubs', color: 'bg-blue-600' },
    { id: 'flower', name: 'Flowers', color: 'bg-purple-600' },
    { id: 'succulent', name: 'Succulents', color: 'bg-yellow-600' },
    { id: 'palm', name: 'Palms', color: 'bg-orange-600' }
  ]

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Plants</p>
              <p className="text-3xl font-bold text-white mt-1">{stats.totalPlants}</p>
            </div>
            <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Categories</p>
              <p className="text-3xl font-bold text-white mt-1">{Object.keys(stats.categories).length}</p>
            </div>
            <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
              <Database className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">This Month</p>
              <p className="text-3xl font-bold text-white mt-1">12</p>
            </div>
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Active</p>
              <p className="text-3xl font-bold text-white mt-1">8</p>
            </div>
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
              <Settings className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">Plants by Category</h3>
        <div className="space-y-3">
          {categories.map((cat) => (
            <div key={cat.id} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 ${cat.color} rounded`}></div>
                <span className="text-gray-300">{cat.name}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-white font-medium">{stats.categories[cat.id] || 0}</span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {stats.recentActivity.map((plant, index) => (
            <div key={plant.id || index} className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Leaf className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{plant.name}</p>
                <p className="text-gray-400 text-sm">{plant.scientificName}</p>
              </div>
              <span className="text-gray-400 text-sm">{plant.category}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Plants View Component  
const PlantsView = ({ plants, viewMode, handleEdit, handleDelete, filterCategory, setFilterCategory, categories }) => {
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center space-x-3">
        <span className="text-gray-400 text-sm">Filter by:</span>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setFilterCategory(cat.id)}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              filterCategory === cat.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Plants Grid/List */}
      {plants.length > 0 ? (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
          : 'space-y-4'
        }>
          {plants.map((plant) => (
            <div key={plant.id} 
              className={`bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-gray-600 transition-colors ${
                viewMode === 'list' ? 'flex' : ''
              }`}
            >
              {/* Image */}
              {plant.image && (
                <div className={`${viewMode === 'grid' ? 'aspect-square' : 'w-24 h-24'} overflow-hidden`}>
                  <img 
                    src={plant.image} 
                    alt={plant.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Content */}
              <div className={`p-4 ${viewMode === 'list' ? 'flex-1 flex items-center justify-between' : ''}`}>
                <div className={viewMode === 'list' ? 'flex-1' : ''}>
                  <h3 className="text-white font-semibold text-lg truncate">{plant.name}</h3>
                  <p className="text-gray-400 text-sm italic">{plant.scientificName}</p>
                  {viewMode === 'list' && (
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center space-x-1">
                        <MapPin className="w-3 h-3" />
                        <span>{plant.habitat}</span>
                      </span>
                      <span className={`px-2 py-1 rounded-full ${
                        plant.careLevel === 'Easy' ? 'bg-green-600 text-white' :
                        plant.careLevel === 'Moderate' ? 'bg-yellow-600 text-white' :
                        'bg-red-600 text-white'
                      }`}>
                        {plant.careLevel}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className={`flex items-center space-x-2 ${viewMode === 'grid' ? 'mt-4' : ''}`}>
                  <button
                    onClick={() => handleEdit(plant)}
                    className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(plant.id)}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Leaf className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No plants found</h3>
          <p className="text-gray-400">Add your first plant to get started</p>
        </div>
      )}
    </div>
  )
}

// Placeholder Views
const ReforestationsView = () => (
  <div className="text-center py-12">
    <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-white mb-2">Reforestation Projects</h3>
    <p className="text-gray-400">Coming soon...</p>
  </div>
)

const SettingsView = () => (
  <div className="text-center py-12">
    <Settings className="w-16 h-16 text-gray-600 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-white mb-2">Settings</h3>
    <p className="text-gray-400">Configuration panel coming soon...</p>
  </div>
)

// Plant Modal Component
const PlantModal = ({ formData, handleInputChange, handleSubmit, onClose, editingPlant }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">
            {editingPlant ? 'Edit Plant' : 'Add New Plant'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="Common name"
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Scientific Name *</label>
            <input
              type="text"
              name="scientificName"
              value={formData.scientificName}
              onChange={handleInputChange}
              required
              className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="Scientific name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              >
                <option value="tree">Tree</option>
                <option value="shrub">Shrub</option>
                <option value="flower">Flower</option>
                <option value="succulent">Succulent</option>
                <option value="palm">Palm</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Care Level</label>
              <select
                name="careLevel"
                value={formData.careLevel}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              >
                <option value="Easy">Easy</option>
                <option value="Moderate">Moderate</option>
                <option value="Difficult">Difficult</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="Plant description"
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Habitat</label>
            <input
              type="text"
              name="habitat"
              value={formData.habitat}
              onChange={handleInputChange}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="Natural habitat"
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Image URL</label>
            <input
              type="url"
              name="image"
              value={formData.image}
              onChange={handleInputChange}
              className="w-full bg-gray-700 text-white border border-gray-600 rounded-lg px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div className="flex items-center space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
            >
              {editingPlant ? (
                <>
                  <Save className="w-4 h-4" />
                  <span>Update</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Add Plant</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminMinimalist
